import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment";

import { createUser, createServiceUser } from "actions/Users";
import { assignUnit } from "actions/Units";
import { submitNotificationPerformance } from "actions/App";

import AddUserType from "components/AddUserType";
import AddUserInfo from "components/AddUserInfo";
import AddUserConfirm from "components/AddUserConfirm";

const AddUser = ({ onClose, facility, type, preSelectedUnit }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const country = useSelector((state) => state.app.country);
  const schedules = useSelector((state) => state.settings.siteSchedules);
  const [currentStep, setCurrentStep] = useState(type ? "info" : "type");

  const [formData, setFormData] = useState({
    userType: type ? type : "", // tenant, employee, limited
    firstName: "",
    lastName: "",
    email: "",
    country: country,
    phone: "",
    accessCode: "",
    role: "",
    expirationDate: null,
    selectedUnits: preSelectedUnit ? [preSelectedUnit] : [],
    selectedZones: [],
    selectedSchedule: null,
    assignToAllSites: false,
  });

  const goTo = (step) => setCurrentStep(step);

  const updateForm = (field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  const resetForm = () => {
    setFormData({
      userType: "",
      firstName: "",
      lastName: "",
      email: "",
      country: country,
      phone: "",
      accessCode: "",
      role: "",
      expirationDate: null,
      selectedUnits: [],
      selectedZones: [],
      selectedSchedule: null,
      assignToAllSites: false,
    });
  }

  const submitForm = () => {
    dispatch(submitNotificationPerformance(
      {
        site_id: facility.id,
        notification_id: formData.phone && formData.phone !== "" ? formData.phone : formData.email,
        notification_type: formData.userType === "tenant" ? "unitNotifications" : "account_activation",
        notification_name: "new_account",
        start_time: moment().utc()
      }
    ));
    if (formData.userType === "tenant") {
      dispatch(assignUnit(
        {
          accessCode: formData.accessCode,
          companyUUID: facility.companyUUID,
          countryCode: formData.country.phone[0].toString(),
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          roleUUID: formData.role.uuid,
          unitUUID: formData.selectedUnits[0]
        },
        {
          facilityUUID: facility.uuid,
          companyUUID: facility.companyUUID,
          messageOne: intl.formatMessage({id: "notifications.success.assign.workDone"}),
          messageTwo: intl.formatMessage({id: "notifications.success.assign"})
        }
      ));
    }
    if (formData.userType === "employee") {
      dispatch(createUser(
        {
          accessCode: formData.accessCode,
          companyUUID: facility.companyUUID,
          countryCode: formData.country.phone[0].toString(),
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          loginType: "noke",
          phone: formData.phone,
          role: formData.role.uuid,
          roleAtAllSites: formData.assignToAllSites,
        },
        formData.selectedSchedule
          ? formData.selectedSchedule[0]
          : schedules && schedules.length > 0
          ? schedules[1].userSiteHoursSuiteUUID
          : null,
        {
          facilityUUID: facility.uuid,
          companyUUID: facility.companyUUID,
          messageOne: intl.formatMessage({id: "notifications.success.create.workDone"}),
          messageTwo: intl.formatMessage({id: "notifications.success.edit"})
        }
      ));
    }
    if (formData.userType === "limited") {
      dispatch(createServiceUser(
        {
          accessCode: formData.accessCode,
          companyUUID: facility.companyUUID,
          countryCode: formData.country.phone[0].toString(),
          email: formData.email,
          expiration: formData.expirationDate && formData.expirationDate.utcOffset(0).endOf("day"),
          firstName: formData.firstName,
          lastName: formData.lastName,
          loginType: "noke",
          phone: formData.phone,
          role: formData.role.uuid,
          zoneUUIDs: formData.selectedZones
        },
        {
          facilityUUID: facility.uuid,
          companyUUID: facility.companyUUID,
          messageOne: intl.formatMessage({id: "notifications.success.create.workDone"}),
          messageTwo: intl.formatMessage({id: "notifications.success.addServiceUser"})
        }
      ));
    }
    onClose();
  }

  return (
    <>
      {currentStep === "type" && (
        <AddUserType
          updateForm={updateForm}
          onNext={() => goTo("info")}
          facility={facility}
        />
      )}

      {currentStep === "info" && (
        <AddUserInfo
          formData={formData}
          updateForm={updateForm}
          facility={facility}
          preSelectedUnit={preSelectedUnit}
          onNext={() => goTo("confirm")}
          onBack={() => {resetForm(); goTo("type")}}
          onClose={onClose}
        />
      )}

      {currentStep === "confirm" && (
        <AddUserConfirm
          formData={formData}
          preSelectedUnit={preSelectedUnit}
          updateForm={updateForm}
          facility={facility}
          onBack={() => goTo("info")}
          onSubmit={submitForm}
        />
      )}
    </>
  )
}

export default AddUser;